<template>
    <v-dialog v-model="showShortenPopup" :width="700">
        <div class="dynamic-popup">
            <div class="popup-header">
                <div class="popup-title">
                    <Svg class="dynamic" :src="require('@/assets/icons/shorten.svg')" fill='#030712'></Svg>
                    <span>Narrative Spine</span>
                </div>
            </div>
            <div class="popup-subtitle">
                <span>
                    Narrative Spine creates a summarized, sharper version of the finalized narrative by highlighting the
                    key
                    context, strategy, and vision.
                </span>
            </div>
            <div class="popup-content">
                <div style="padding-left: 1rem;">
                    <li>Works only on the finalized version</li>
                    <li>Can be used once per narrative</li>
                    <li>The narrative spine is saved and accessible anytime</li>
                </div>
            </div>
            <div class="footer-btn">
                <button class="standard" @click="closePopup()">
                    <div class="standard-title"> Cancel</div>
                </button>
                <button class="fine-tune" @click="generateShorten()">
                    <div class="fine-tune-title"> Generate Spine</div>
                </button>

            </div>
        </div>
    </v-dialog>
    <v-dialog v-model="showIntroDialog" :width="650">
        <div class="dynamic-popup comments-intro">
            <div class="popup-header">
                <div class="popup-title">
                    <img :src="require('@/assets/icons/comment-info.svg')" />
                    <span>Introducing Comments</span>
                </div>

                <button class="close-btn" @click="closeIntroDialog()">
                    <img :src="require('@/assets/icons/close.svg')" alt="">
                </button>
            </div>

            <div class="popup-subtitle">
                <span>
                    Highlight lines needing changes. Provide context for AI to understand better. Generate all changes
                    together.
                </span>
            </div>

            <div class="popup-content">
                <img :src="require('@/assets/images/comments.png')" />
            </div>
        </div>
    </v-dialog>
    <!-- Add Comment Dialog
    <v-dialog v-model="showCommentInput" :width="400" persistent>
        <div class="comment-input-popup">
            <div class="popup-title">
                <img :src="require('@/assets/icons/comment-black.svg')">
                {{ editingCommentId ? 'Edit Comment' : 'Add Comment' }}
            </div>

            <div class="popup-body">
                <textarea v-model="newCommentText" class="comment-input" placeholder="eg: Rephrase the paragraph"
                    rows="4" autofocus></textarea>
            </div>

            <div class="popup-actions">
                <button class="action-btn discard" @click="discardComment()">
                    Discard
                </button>
                <button class="action-btn add" @click="addComment()" :disabled="!newCommentText.trim() || (editingCommentId && newCommentText === editingCommentText)">
                    {{ editingCommentId ? 'Edit Comment' : 'Add Comment' }}
                </button>
            </div>
        </div>
    </v-dialog>
-->
    <div class="narrative-page-wrapper">
        <CommentsIntroHeader v-if="!commentsVisited" @open-tutorial="openIntroDialog()" @close="showIntroPopup()" />

        <div :class="['completed-narrative-container', { 'not-complete': !isCompleted }]" ref="pageScrollContainer">

            <div v-if="isCompleted" class="completed-narrative-header">
                <div class="completed-narrative-header-left">
                    <div class="doc-name-editor-wrapper">
                        <span class="narrative-title">{{ narrativeName }}</span>
                        <RenamePopup v-if="isTitleEditing" :docName="narrativeName" :isEditing="isTitleEditing"
                            @update-isEdit="isTitleEditing = $event" @updated-prTitle="handleTitleUpdate"
                            flowType="narrative" />
                        <button class="edit-btn" @click="editTitle">
                            <img :src="require('@/assets/icons/edit.svg')" />
                        </button>
                    </div>
                </div>
            </div>
            <div class="final-narrative-header" v-else-if="!isCompleted">
                <div class="narrative-final-header-left">
                    <img :src="require('@/assets/icons/pr-completed.svg')" />
                    <div class="completed-msg">
                        <h2 class="completed-title">Narrative Completed</h2>
                    </div>
                </div>
                <div class="narrative-final-header-right">
                    <div class="narrative-final-btn-wrapper">
                        <img :src="require('@/assets/icons/share.svg')"></img>
                        <p class="home-btn-text">Share</p>
                    </div>

                    <button class="narrative-final-btn-wrapper" @click="goToHome()">
                        <img :src="require('@/assets/icons/home.svg')" alt="">
                        <p class="home-btn-text">Go to Dashboard</p>
                    </button>

                    <button class="narrative-final-btn-wrapper" :disabled="downloadLoader" @click="downloadNarrative()">
                        <v-progress-circular v-if="downloadLoader" indeterminate :size="18"
                            :width="2"></v-progress-circular>
                        <div v-else class="download-wrapper">
                            <Svg :src="require('@/assets/icons/download.svg')" fill="white"></Svg>
                            <p>Download</p>
                        </div>
                    </button>
                </div>
            </div>

            <div class="final-narrative-container">
                <div class="final-narrative-wrapper">
                    <div class="version-toggle-container">
                        <div class="version-toggle">
                            <button :class="['toggle-btn', { 'active': showOriginal }]" @click="toggleNarratives(true)"
                                :disabled="isEditing" v-if="generatedVersions.length === 0">
                                Finalized Version
                            </button>
                            <div class="version-controls toggle-btn"
                                :class="{ active: showOriginal || activeVersion.startsWith('version') }"
                                v-if="generatedVersions.length > 0">
                                <!-- LEFT ARROW (not on Finalized) -->
                                <button v-if="currentVersionIndex > -1" class="arrow-btn"
                                    :class="{ inverted: showOriginal || activeVersion.startsWith('version') }"
                                    @click.stop="navigateVersion(-1)" :disabled="isEditing">
                                    <img :src="require('@/assets/icons/left-arrow.svg')" />
                                </button>

                                <!-- LABEL -->
                                <span class="version-label" @click="toggleNarratives(true)">
                                    {{
                                        currentVersionIndex === -1
                                            ? 'Finalized Version'
                                            : `Version ${generatedVersions[currentVersionIndex]?.version}`
                                    }}
                                </span>

                                <!-- RIGHT ARROW (only if next version exists) -->
                                <button v-if="currentVersionIndex < generatedVersions.length - 1" class="arrow-btn"
                                    :class="{ inverted: showOriginal || activeVersion.startsWith('version') }"
                                    @click.stop="navigateVersion(1)" :disabled="isEditing">
                                    <img :src="require('@/assets/icons/right-arrow.svg')" />
                                </button>
                            </div>



                            <button :class="['toggle-btn', { 'active': activeVersion === alternateVersionKey }]"
                                @click="toggleNarratives(alternateVersionKey)" :disabled="isEditing">
                                {{ alternateVersionLabel }}
                            </button>
                            <button v-if="shortenedContent"
                                :class="['toggle-btn', { 'active': activeVersion === 'spine' }]"
                                @click="toggleNarratives('spine')" :disabled="isEditing">
                                Spine
                            </button>
                            <button v-if="mediaPitchContent"
                                :class="['toggle-btn', { 'active': activeVersion === 'mediaPitch' }]"
                                @click="toggleNarratives('mediaPitch')" :disabled="isEditing">
                                Media Pitch
                            </button>

                        </div>
                        <div class="left-header">
                            <div v-if="!isEditing" class="header-right">
                                <v-tooltip v-if="!canEditSelectedVersion()" location="bottom"
                                    content-class="edit-tooltip-content">
                                    <template #activator="{ props }">
                                        <span v-bind="props">
                                            <button class="edit-btn" disabled>
                                                <Svg :src="EditIcon" fill="black"></Svg>
                                            </button>
                                        </span>
                                    </template>

                                    <span class="edit-tooltip-text">
                                        Editing is disabled because comments are already added.
                                    </span>
                                </v-tooltip>


                                <button v-else class="edit-btn" @click="editPr(activeVersion)">
                                    <Svg :src="EditIcon" fill="black"></Svg>
                                </button>
                            </div>


                            <div v-else class="header-right">
                                <button class="save-changes-btn" @click="saveNarrative(activeVersion)"
                                    :disabled="isEditing && previousVersion === editedVersion && !activeVersion.startsWith('version')">
                                    <Svg :src="require('@/assets/icons/save.svg')" fill="#262626"></Svg>
                                    Save Changes
                                </button>
                                <button class="edit-btn" @click="cancelPrChanges()">
                                    <img :src="require('@/assets/icons/close.svg')"></img>
                                </button>
                            </div>
                            <div v-if="!shortenedContent || !mediaPitchContent" class="dropdown-wrapper">
                                <button class="shorten" @click="toggleDropdown">
                                    <p>Options</p>
                                    <img :src="require('@/assets/icons/dropdown.svg')" />
                                </button>

                                <div class="dropdown-menu" v-if="isOpen">
                                    <p class="dropdown-title">Generate options</p>

                                    <div v-if="!shortenedContent" class="dropdown-item" @click="handleShorten">
                                        <img :src="require('@/assets/icons/shorten.svg')" />
                                        <span>Generate Spine</span>
                                    </div>

                                    <div v-if="!mediaPitchContent" class="dropdown-item" @click="handleMediaPitch">
                                        <img :src="require('@/assets/icons/pitch-icon.svg')" />
                                        <span>Generate Media Pitch</span>
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>
                    <div class="container">
                        <div class="narrative-details" ref="narrativeScroll"
                            :class="{ 'finalized-narrative-details': showOriginal }" @mouseup="checkSelection"
                            @mousedown.self="closeContextMenu">
                            <div class="narrative-details-wrapper" v-html="currentContent" ref="detailsContainer"
                                :key="refresh" id="edit-container" v-if="!isEditing"></div>
                            <div v-show="isEditing" class="editor-wrapper">
                                <div ref="editorContainer">
                                    <div ref="editor" class="quill-editor"></div>
                                </div>
                            </div>
                            <DragActionPopup ref="contextMenu" v-if="popupData" :showMenu="showContextMenu"
                                :menuPosition="menuPosition" :documentData="popupData" :version="activeVersion"
                                :commentedLength="comments.length"
                                :isCommentDisabled="isActionDisabled || isCommentsLocked || selectionOverlapsComment"
                                :isArticleAllowed="isArticleAllowed"
                                :shouldShowOverlapInfo="selectionOverlapsComment && !isActionDisabled"
                                @close="closeContextMenu" @comment-added="handleCommentAdded" />
                        </div>
                        <div class="right-panel">
                            <div class="right-panel-tabs">
                                <div class="tab-item" :class="{ active: rightPanelTab === 'generate' }"
                                    @click="rightPanelTab = 'generate'">
                                    <img :src="require('@/assets/icons/generate-more.svg')" />
                                    Generate More
                                </div>

                                <div class="tab-item" :class="{ active: rightPanelTab === 'comments' }"
                                    @click="rightPanelTab = 'comments'">
                                    Comments
                                </div>
                            </div>
                            <div v-if="rightPanelTab === 'comments' && comments.length != 0 && showCommentInfo"
                                class="comment-info">
                                <img :src="require('@/assets/icons/info-black.svg')" />
                                <span>Comments are supported only on the <span style="font-weight: 600;">"Finalised
                                        version"</span>
                                    and the subsequent versions</span>
                            </div>

                            <div v-if="rightPanelTab === 'generate'" class="panel-content">
                                <div class="meta-data-wrapper">
                                    <div class="assets-container">
                                        <p class="header-title">Source-code Campaigning - generate assets, content and
                                            campaigns
                                            at
                                            scale
                                        </p>
                                        <div class="assets-wrapper" @click="openGeneratePopup('Social Media Campaign')">
                                            <div class="asset-left">
                                                <img :src="require('@/assets/icons/social-media-icon.svg')" />
                                                <p class="asset-type">Generate Social Media Campaign</p>
                                            </div>
                                            <img :src="require('@/assets/icons/create-icon.svg')" />
                                        </div>

                                        <div class="assets-wrapper" @click="openGeneratePopup('PR Strategy Playbook')">
                                            <div class="asset-left">
                                                <img :src="require('@/assets/icons/pr-campaign-icon.svg')" />
                                                <p class="asset-type">Generate PR Strategy</p>
                                            </div>
                                            <img :src="require('@/assets/icons/create-icon.svg')" />
                                        </div>

                                        <div class="assets-wrapper" @click="createArticle()">
                                            <div class="asset-left">
                                                <img :src="require('@/assets/icons/article-icon.svg')" />
                                                <p class="asset-type">Generate Articles</p>
                                            </div>
                                            <img :src="require('@/assets/icons/create-icon.svg')" />
                                        </div>

                                        <div class="assets-wrapper" @click="openGeneratePopup('Storybank')">
                                            <div class="asset-left">
                                                <img :src="require('@/assets/icons/storyBoardIcon.svg')" />
                                                <p class="asset-type">Generate Storybank</p>
                                            </div>
                                            <img :src="require('@/assets/icons/create-icon.svg')" />
                                        </div>
                                    </div>
                                </div>


                                <div v-if="isCompleted">
                                    <div class="details-wrapper">
                                        <div class="meta-data-header">
                                            <p class="header-title">Details</p>
                                        </div>
                                        <div class="details-container">
                                            <div class="meta-wrapper">
                                                <p class="meta-data">Created On</p>
                                                <p class="meta-value">{{ formateDate(finalNarrativeDetails?.createdAt)
                                                    }}</p>
                                            </div>
                                            <div class="meta-wrapper">
                                                <p class="meta-data">Completed On</p>
                                                <p class="meta-value">{{ formateDate(finalNarrativeDetails?.updatedAt)
                                                    }}</p>
                                            </div>
                                            <div class="meta-wrapper">
                                                <p class="meta-data">Created By</p>
                                                <p class="meta-value">{{ finalNarrativeDetails?.createdByName }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="rightPanelTab === 'comments'" class="panel-content comments-panel">
                                <!-- LIMIT REACHED STATE -->
                                <div v-if="rightPanelTab === 'comments' && !comments.length && isCommentsLocked && activeVersion.startsWith('version')"
                                    class="empty-comments">
                                    <img :src="require('@/assets/icons/comment-grayscale.webp')"
                                        class="empty-comments-img" />
                                    <h3 class="empty-title">Version limit reached.</h3>
                                    <p class="empty-desc">
                                        You’ve hit the maximum number of comment-based changes allowed for this
                                        finalised
                                        version.

                                    </p>
                                </div>

                                <!-- STANDARD INTRO STATE -->
                                <div v-else-if="!comments.length" class="empty-comments">
                                    <img :src="require('@/assets/icons/comment-info.svg')" class="empty-comments-img" />
                                    <h3 class="empty-title">Introducing Comments</h3>
                                    <p class="empty-desc">
                                        Highlight the lines that need adjustments and give context for the AI to
                                        comprehend.
                                        Users
                                        can add several comments simultaneously to implement all changes at once.
                                    </p>
                                    <img class="tour-image" :src="require('@/assets/images/comments.png')" />
                                    <div class="empty-comments-info">
                                        <img :src="require('@/assets/icons/info-icon.svg')" />
                                        <span>Comments are supported on the original version as well as all subsequent
                                            versions</span>
                                    </div>
                                </div>


                                <div v-else class="comments-list" ref="commentsList">

                                    <div class="comment-card" v-for="(comment, index) in comments"
                                        :key="comment.id || index" :class="{ active: activeCommentId === comment.id }"
                                        :data-comment-id="comment.id" @click="onCommentClick(comment)">

                                        <div class="comment-header">
                                            <div class="comment-top-row">
                                                <span class="comment-time">{{ comment.time }}</span>

                                                <div class="comment-actions" v-if="!isActionDisabled">
                                                    <template v-if="editingCommentId === comment.id">
                                                        <button class="icon-btn"
                                                            @click.stop="saveEditedComment(comment)"
                                                            :disabled="isSaveCommentDisabled">
                                                            <Svg :src="require('@/assets/icons/save.svg')"
                                                                fill="#fff"></Svg> </button>

                                                        <button class="icon-btn" @click.stop="cancelEditComment">
                                                            <img :src="require('@/assets/icons/cancel.svg')"
                                                                class="cancel-img" />
                                                        </button>
                                                    </template>

                                                    <template v-else>
                                                        <button class="icon-btn" @click.stop="editComment(comment)">
                                                            <img :src="require('@/assets/icons/edit.svg')" />
                                                        </button>
                                                        <button class="icon-btn" @mousedown.stop
                                                            @click.stop="handleDeleteComment(comment.id)">
                                                            <img :src="require('@/assets/icons/delete.svg')" />
                                                        </button>
                                                    </template>
                                                </div>
                                            </div>

                                            <p class="comment-highlight">
                                                {{ comment.highlightedText }}
                                            </p>
                                        </div>

                                        <div class="comment-reply">
                                            <img class="reply-arrow" :src="require('@/assets/icons/reply-arrow.svg')" />
                                            <textarea v-if="editingCommentId === comment.id" v-model="editingReplyText"
                                                class="reply-textarea" rows="3" @click.stop />

                                            <span v-else class="reply-text">{{ comment.comment }}</span>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div v-if="rightPanelTab === 'comments' && comments.length > 0 && !isActionDisabled"
                                class="comment-actions-bar">
                                <button class="secondary-btn" @click="handleRemoveAllComment"
                                    :disabled="isCommentEditing">Remove
                                    All</button>
                                <button class="primary-btn"
                                    :disabled="generatedVersions.length >= 2 || isCommentEditing"
                                    @click="generateCommentsVersion">
                                    Generate
                                    <img class="primary-btn-image" :src="require('@/assets/icons/generate.svg')" />
                                </button>
                            </div>
                        </div>

                    </div>

                </div>
            </div>
        </div>
    </div>
    <CommonPopup v-if="showCommonPopup" :showCommonPopup="showCommonPopup" :popupType="popupType"
        @confirm-action="handlePopupConfirmation" />

    <!-- Generate Popup Component -->
    <GeneratePopup v-if="generatePopup" v-model="generatePopup" :flowType="selectedFlowType"
        :documentData="documentData">
    </GeneratePopup>
    <v-overlay :model-value="pageLoader" class="overlay" persistent="true">
        <RotatingLoader :size="30" :speed="0.6" />
        <span>Generating</span>
    </v-overlay>
</template>

<script>
import EditIcon from '@/assets/icons/edit.svg';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import moment from 'moment-timezone';
import RenamePopup from '@/components/RenamePopup.vue';
import CommonPopup from '@/components/CommonPopup.vue';
import GeneratePopup from '@/components/GeneratePopup.vue';
import axiosInstance from '@/axiosInterceptor';
import DragActionPopup from '@/components/DragPopup.vue';
import { Eventbus } from '@/plugins/eventBus';
import RotatingLoader from '@/components/CircularProgressBar.vue';
import { downloadPr } from '@/helper/downloadPr';
import CommentsIntroHeader from '@/components/comments/CommentsIntroHeader.vue';
export default {

    components: {
        RenamePopup,
        CommonPopup,
        GeneratePopup,
        DragActionPopup,
        RotatingLoader,
        CommentsIntroHeader,
    },
    props: {
        isCompleted: {
            type: Boolean
        },
        modelValue: {
            type: Object
        },
        title: {
            type: String
        }
    },
    data() {
        return {
            isOpen: false,
            EditIcon,
            activeVersion: 0,
            editableText: null,
            saveLoader: false,
            editor: null,
            isEditing: false,
            originalPr: null,
            refresh: 0,
            shareUrl: null,
            originalTitle: this.modelValue?.prName,
            newTitle: null,
            isTitleEditing: false,
            saveDocTitleLoader: false,
            showCommonPopup: false,
            popupType: null,
            previousVersion: null,
            editedVersion: null,
            narrativeName: null,
            downloadLoader: false,
            showOriginal: true,
            generatePopup: false,
            selectedFlowType: '',
            documentData: null,
            showContextMenu: false,
            menuPosition: { left: 0, top: 0 },
            selectedText: '',
            popupData: null,
            shortenNarrative: null,
            showShortenPopup: false,
            pageLoader: false,
            shortenedContent: null,
            mediaPitchContent: null,
            finalizedVersion: null,
            activeVersion: 'final',
            alternateContent: null,
            finalNarrative: null,
            comments: [],
            activeCommentId: null,
            rightPanelTab: 'generate',
            showPopup: true,
            showIntroDialog: false,
            // showCommentInput: false,
            // newCommentText: '',
            selectedCommentData: null,
            editingCommentId: null,
            editingCommentText: '',
            isDeleting: false,
            showCommentInfo: true,
            currentVersionIndex: -1,
            originalReplyText: '',
            editingReplyText: '',
            selectionOverlapsComment: false,
            commentsVisited: null,
            commentToDelete: null,
            localCommentsVersions: [],
            versionLock: false,
        }
    },
    computed: {
        isVersionToggleActive() {
            return this.showOriginal || this.activeVersion.startsWith('version')
        },
        finalNarrativeDetails() {
            return this.modelValue
        },
        alternateVersionLabel() {
            return this.finalizedVersion === 'ORG' ? 'Alternate Voice' : 'Original Version';
        },
        alternateVersionKey() {
            return this.finalizedVersion === 'ORG' ? 'alternate' : 'original';
        },
        currentContent() {
            if (this.currentVersionIndex === -1) {
                // Show Final/Original
                switch (this.activeVersion) {
                    case 'final':
                        return this.finalNarrative || '';
                    case 'spine':
                        return this.shortenedContent || '';
                    case 'alternate':
                        return this.alternateContent || '';
                    case 'original':
                        return this.alternateContent || '';
                    case 'mediaPitch':
                        return this.mediaPitchContent || '';
                    default:
                        return this.finalNarrative || '';
                }
            } else {
                // Show Generated Version
                const version = this.generatedVersions[this.currentVersionIndex];
                return version ? version.commentedContent : '';
            }
        },
        generatedVersions() {
            return this.localCommentsVersions;
        },
        isCommentsLocked() {
            return this.generatedVersions.length >= 2;
        },
        isActionDisabled() {
            if(this.isCommentsLocked) return true;
            if (this.versionLock) return true;

            let latestVersion = 'final';

            if (this.generatedVersions.length > 0) {
                const last = this.generatedVersions[this.generatedVersions.length - 1];
                latestVersion = `version${last.version}`;
            }

            return this.activeVersion !== latestVersion;
        },
        isSaveCommentDisabled() {
            if (!this.editingCommentId) return true

            return (
                this.editingReplyText.trim() ===
                this.originalReplyText.trim()
            )
        },
        showCommentIntro() {
            return !this.visitedModulesProp?.COMMENTS;
        },
        isArticleAllowed() {
            return this.activeVersion === 'final';
        },
        isCommentEditing() {
            return !!this.editingCommentId
        },

    },
    watch: {

        activeVersion() {
            this.fetchComments();
            this.$nextTick(() => this.applyComments(true))
        },
        comments() {
            if (!this.isDeleting) {
                this.$nextTick(() => this.applyComments())
            }
        },
        modelValue: {
            immediate: true,
            deep: true,
            handler(val) {
                if (this.isEditing) return
                if (!val) return
                if (typeof val?.commentsVisited === 'boolean') {
                    this.commentsVisited = val.commentsVisited
                }

                this.narrativeName = val.narrativeName
                this.finalNarrative = val.finalizedNarrative?.narrativeData
                this.finalizedVersion = val.finalizedNarrative?.finalizedVersion

                this.shortenedContent =
                    val.shortenNarrativeData ?? this.shortenedContent

                this.mediaPitchContent =
                    val.mediaPitchData ?? this.mediaPitchContent

                this.alternateContent =
                    this.finalizedVersion === 'ORG'
                        ? val.humanizedNarrativeData
                        : val.originalNarrativeData

                if (Array.isArray(val.commentsVersion)) {
                    const incoming = val.commentsVersion

                    // Merge instead of replace
                    incoming.forEach(v => {
                        const exists = this.localCommentsVersions.find(
                            lv => String(lv.version) === String(v.version)
                        )
                        if (!exists) {
                            this.localCommentsVersions.push(v)
                        }
                    })
                }
            }
        }
    },
    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen;
        },
        navigateVersion(direction) {
            const newIndex = this.currentVersionIndex + direction;
            if (newIndex < -1 || newIndex >= this.generatedVersions.length) return;

            this.currentVersionIndex = newIndex;

            if (newIndex === -1) {
                this.activeVersion = 'final';
                this.showOriginal = true;
            } else {
                const v = this.generatedVersions[newIndex];
                this.activeVersion = `version${v.version}`;
                // Keep showOriginal FALSE for generated versions so they don't look like 'Finalized' in logic, 
                // BUT we want the button to look active. The button class logic handles the visual state.
                this.showOriginal = false;
            }
            this.$emit('download-version', this.activeVersion);
            this.$nextTick(() => this.applyComments(true));
        },

        async handleMediaPitch() {
            this.isOpen = false;
            await this.generateMediaPitch()

        },
        async saveArticleDraft(articleId) {
            let endpoint = '/article/saveDraft'
            let payload = {
                articleId: articleId,
            }
            let documentData = {};
            documentData = {
                documentId: this.$route.params.narrativeId,
                documentType: 'BUISNESS_NARRATVE',
                section: this.selectedText,
            }

            payload.lastUpdatedStage = 'SOURCE_DOC';
            payload.documentData = documentData


            await axiosInstance.put(endpoint, payload)
                .then((response) => {
                    this.showArticleStage = true;
                    this.$router.push({ name: 'ArticleStages', params: { articleId: articleId }, state: { locked: true } });
                })
                .catch((error) => {
                    console.error('[ERROR] ', error);
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'error',
                        message: 'Failed to save changes',
                    });
                })
        },

        async createArticle() {
            this.pageLoader = true;
            let endpoint = '/article/create';
            let payload = {
                articleDocumentName: null
            }
            await axiosInstance.post(endpoint, payload)
                .then(async (response) => {
                    this.articleId = response.data.data.articleId;
                    if (response.status === 201) {
                        await this.saveArticleDraft(this.articleId)
                    }
                })
                .catch((error) => {
                    Eventbus.emit({
                        snackType: 'error',
                        message: 'Failed to create new article'
                    })
                })
                .finally(() => {
                    this.pageLoader = false;

                })

        },

        handleShorten() {
            this.isOpen = false;
            this.showShortenPopup = true;
        },
        closePopup() {
            this.showShortenPopup = false;
        },
        async generateMediaPitch() {
            try {
                this.pageLoader = true;
                const response = await axiosInstance.post('/mediaPitch/generate', {
                    inputId: this.$route.params.narrativeId,
                    moduleType: 'NAR'
                });
                if (response.status == 200) {
                    this.mediaPitchContent = response.data.data.mediaPitch.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '').replace(/\n/g, '').trim();
                    this.activeVersion = 'mediaPitch';
                    this.$emit('download-version', this.activeVersion);
                    this.showOriginal = false;
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: 'Media Pitch version generated successfully',
                    });
                }

            } catch (error) {
                if (error.response?.status === 502 || error.code === 'ERR_NETWORK' || error.response?.status === 504) {
                    let attempts = 0;
                    let maxAttempts = 40;
                    const intervalTime = 15000;
                    const intervalId = setInterval(async () => {
                        attempts++;
                        try {
                            this.$emit('update:modelValue', this.modelValue);
                            if (this.shortenedContent && this.shortenedContent.trim() !== "") {
                                clearInterval(intervalId);
                                this.activeVersion = 'mediaPitch';
                                this.$emit('download-version', this.activeVersion);
                                this.showOriginal = false;
                                Eventbus.emit('commonSnackbar', {
                                    snackType: 'save',
                                    message: 'Media Pitch version generated successfully',
                                });

                            } else if (attempts >= maxAttempts) {
                                clearInterval(intervalId);
                                this.pageLoader = false;
                            }

                        } catch (pollError) {
                            console.error("Error during polling:", pollError);
                            clearInterval(intervalId);
                            this.pageLoader = false;
                            Eventbus.emit('commonSnackbar', {
                                snackType: 'error',
                                message: 'Failed to generate PR. Please try again later.'
                            });
                        }
                    }, intervalTime);
                }
            } finally {
                this.pageLoader = false;


            }
        },
        async generateShorten() {
            try {
                this.pageLoader = true;
                this.showShortenPopup = false;
                const response = await axiosInstance.post('/narrative/shorten', {
                    narrativeId: this.$route.params.narrativeId
                });
                if (response.status == 200) {
                    this.shortenedContent = response.data.data.shortHumanisedContent.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '').replace(/\n/g, '').trim();
                    this.activeVersion = 'spine';
                    this.$emit('download-version', this.activeVersion);
                    this.showOriginal = false;
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: 'Spine version generated successfully',
                    });
                }

            } catch (error) {
                if (error.response?.status === 502 || error.code === 'ERR_NETWORK' || error.response?.status === 504) {
                    let attempts = 0;
                    let maxAttempts = 40;
                    const intervalTime = 15000;
                    const intervalId = setInterval(async () => {
                        attempts++;
                        try {
                            this.$emit('update:modelValue', this.modelValue);
                            if (this.shortenedContent && this.shortenedContent.trim() !== "") {
                                clearInterval(intervalId);
                                this.activeVersion = 'spine';
                                this.$emit('download-version', this.activeVersion);
                                this.showOriginal = false;
                                Eventbus.emit('commonSnackbar', {
                                    snackType: 'save',
                                    message: 'Spine version generated successfully',
                                });

                            } else if (attempts >= maxAttempts) {
                                clearInterval(intervalId);
                                this.pageLoader = false;
                            }

                        } catch (pollError) {
                            console.error("Error during polling:", pollError);
                            clearInterval(intervalId);
                            this.pageLoader = false;
                            Eventbus.emit('commonSnackbar', {
                                snackType: 'error',
                                message: 'Failed to generate PR. Please try again later.'
                            });
                        }
                    }, intervalTime);
                }
            } finally {
                this.pageLoader = false;


            }
        },
        toggleNarratives(version) {
            if (version === true || version === 'final') {
                if (this.generatedVersions.length > 0 && this.currentVersionIndex !== -1) {
                    this.currentVersionIndex = -1;
                    this.activeVersion = 'final';
                    this.showOriginal = true;
                } else {
                    this.activeVersion = 'final';
                    this.showOriginal = true;
                    this.currentVersionIndex = -1;
                }
            } else if (version === 'alternate' || version === 'original') {
                this.activeVersion = version;
                this.showOriginal = false;
                this.currentVersionIndex = -1;
            } else if (version === 'spine' || version === false) {
                this.activeVersion = version === false ? 'spine' : version;
                this.showOriginal = false;
                this.currentVersionIndex = -1;
            } else if (version === 'mediaPitch') {
                this.activeVersion = 'mediaPitch';
                this.showOriginal = false;
                this.currentVersionIndex = -1;
            }
            else {
                this.activeVersion = 'final';
                this.showOriginal = true;
                this.currentVersionIndex = -1;
            }
            this.$emit('download-version', this.activeVersion);
        },

        openGeneratePopup(flowType) {
            this.selectedFlowType = flowType;
            this.generatePopup = true;
        },
        checkSelection() {

            const allowedVersions = [
                'final',
                ...this.generatedVersions.map(v => `version${v.version}`)
            ]

            if (!allowedVersions.includes(this.activeVersion) || this.isEditing) {
                this.showContextMenu = false
                return
            }

            const selection = window.getSelection()
            if (!selection || selection.rangeCount === 0) {
                this.showContextMenu = false
                return
            }

            const range = selection.getRangeAt(0)


            const selectedText = selection
                .toString()
                .replace(/\u00A0/g, ' ')
                .trim()

            if (!selectedText || !/\S/.test(selectedText)) {
                this.showContextMenu = false
                return
            }

            const container = this.$refs.detailsContainer
            const scrollContainer = this.$refs.narrativeScroll

            if (
                !container ||
                !scrollContainer ||
                !container.contains(range.startContainer) ||
                !container.contains(range.endContainer)
            ) {
                this.showContextMenu = false
                return
            }

            const { startOffset, endOffset } = this.getOffsets(range)


            const hasOverlap = this.comments?.some(
                c => startOffset < c.endOffset && endOffset > c.startOffset
            )

            this.selectionOverlapsComment = !!hasOverlap

            const rects = range.getClientRects()
            if (!rects.length) {
                this.showContextMenu = false
                return
            }

            const lastRect = rects[rects.length - 1]
            const containerRect = scrollContainer.getBoundingClientRect()

            let top =
                lastRect.bottom -
                containerRect.top +
                scrollContainer.scrollTop +
                8

            let left =
                lastRect.left -
                containerRect.left +
                scrollContainer.scrollLeft

            this.popupData = {
                documentId: this.$route.params.narrativeId,
                documentType: 'BUISNESS_NARRATVE',
                sectionContent: selectedText,
                startOffset,
                endOffset,
                versionType: this.activeVersion,
                hasOverlap
            }

            const isCommentDisabled =
                this.isActionDisabled || this.isCommentsLocked || hasOverlap

            const canShow =
                !isCommentDisabled || this.isArticleAllowed || (hasOverlap && !this.isActionDisabled)

            if (!canShow) {
                this.showContextMenu = false
                return
            }

            this.showContextMenu = true

            this.$nextTick(() => {
                const popupEl = this.$refs.contextMenu?.$el
                if (!popupEl) return

                const popupHeight = popupEl.offsetHeight
                const popupWidth = popupEl.offsetWidth

                // Flip above if overflowing
                if (top + popupHeight > scrollContainer.scrollHeight) {
                    const firstRect = rects[0]
                    top =
                        firstRect.top -
                        containerRect.top +
                        scrollContainer.scrollTop -
                        popupHeight -
                        8
                }

                const maxTop = scrollContainer.scrollHeight - popupHeight - 8
                const maxLeft = scrollContainer.clientWidth - popupWidth - 8

                this.menuPosition = {
                    top: Math.max(8, Math.min(top, maxTop)),
                    left: Math.max(8, Math.min(left, maxLeft))
                }
            })
        }
        ,
        getOffsets(range) {
            const container = this.$refs.detailsContainer
            if (!container) return { startOffset: 0, endOffset: 0 }

            let offset = 0
            const walker = document.createTreeWalker(
                container,
                NodeFilter.SHOW_TEXT,
                null
            )

            while (walker.nextNode()) {
                const node = walker.currentNode

                if (node === range.startContainer) {
                    offset += range.startOffset
                    break
                } else {
                    offset += node.textContent.length
                }
            }

            const length = range.toString().length

            return {
                startOffset: offset,
                endOffset: offset + length
            }
        },

        applyComments(reset = false) {
            const container = this.$refs.detailsContainer
            if (!container) return

            if (reset) {
                container.innerHTML = this.currentContent
            }

            const visibleComments = this.comments
                .filter(c => c.version === this.activeVersion)
                .sort((a, b) => b.startOffset - a.startOffset)

            visibleComments.forEach(c => {
                this.wrapRange(container, c.startOffset, c.endOffset, c.id)
            })

            if (this.activeCommentId) {
                this.setActiveComment(this.activeCommentId)
            }
        },

        wrapRange(container, start, end, commentId) {
            const walker = document.createTreeWalker(
                container,
                NodeFilter.SHOW_TEXT,
                null
            )

            let currentOffset = 0
            const tasks = []

            while (walker.nextNode()) {
                const node = walker.currentNode
                const text = node.textContent || ''
                const length = text.length

                const nodeStart = currentOffset
                const nodeEnd = nodeStart + length

                if (!/\S/.test(text)) {
                    currentOffset += length
                    continue
                }

                if (end > nodeStart && start < nodeEnd) {
                    tasks.push({
                        node,
                        from: Math.max(0, start - nodeStart),
                        to: Math.min(length, end - nodeStart)
                    })
                }

                currentOffset += length
                if (currentOffset >= end) break
            }

            tasks.forEach(({ node, from, to }) => {
                if (from >= to) return

                const text = node.textContent
                const selected = text.slice(from, to)

                if (!selected || !/\S/.test(selected)) return

                const before = text.slice(0, from)
                const after = text.slice(to)

                const parent = node.parentNode
                if (!parent) return

                const frag = document.createDocumentFragment()

                if (before) frag.appendChild(document.createTextNode(before))

                const span = document.createElement('span')
                span.className = 'highlight-comment'
                span.dataset.id = commentId
                span.textContent = selected

                span.addEventListener('click', e => {
                    e.stopPropagation()
                    this.onHighlightClick(commentId)
                })

                frag.appendChild(span)

                if (after) frag.appendChild(document.createTextNode(after))

                parent.replaceChild(frag, node)
            })
        },



        setActiveComment(id, source = 'comment') {
            if (!this.comments.find(c => c.id === id)) return
            this.activeCommentId = id

            const container = this.$refs.detailsContainer
            if (!container) return

            // Clear old highlights
            container
                .querySelectorAll('.highlight-comment.active')
                .forEach(el => el.classList.remove('active'))

            // Activate new highlights
            container
                .querySelectorAll(`span[data-id="${id}"]`)
                .forEach(el => el.classList.add('active'))

            // If highlight clicked → open comments & scroll
            if (source === 'highlight') {
                this.rightPanelTab = 'comments'

                this.$nextTick(() => {
                    this.scrollToComment(id)
                })
            }
        },

        onCommentClick(comment) {
            this.setActiveComment(comment.id, 'comment')

            this.$nextTick(() => {
                this.scrollToHighlight(comment.id, 'comment')
            })
        },

        scrollToHighlight(id) {
            this.$nextTick(() => {
                const container = this.$refs.narrativeScroll
                if (!container) return

                const highlight = container.querySelector(
                    `span[data-id="${id}"]`
                )
                if (!highlight) return

                const containerRect = container.getBoundingClientRect()
                const highlightRect = highlight.getBoundingClientRect()

                const top =
                    highlightRect.top -
                    containerRect.top +
                    container.scrollTop -
                    80   // offset for header padding

                container.scrollTo({
                    top,
                    behavior: 'smooth'
                })

                highlight.classList.add('active-comment-highlight')

                setTimeout(() => {
                    highlight.classList.remove('active-comment-highlight')
                }, 1200)
            })
        },

        onHighlightClick(id) {
            this.setActiveComment(id, 'highlight')

            this.$nextTick(() => {
                this.scrollToHighlight(id, 'highlight')
                this.scrollToComment(id)
            })
        },

        scrollToComment(id) {
            this.$nextTick(() => {
                const list = this.$refs.commentsList
                if (!list) return

                const el = list.querySelector(
                    `.comment-card[data-comment-id="${id}"]`
                )

                if (!el) return

                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            })
        },


        handlePopupConfirmation(val) {
            this.showCommonPopup = false;
            if (val === 'yes') {
                if (this.popupType === 'withoutSave') {
                    this.isEditing = false;
                } else if (this.popupType === 'deleteComment') {
                    this.deleteComment(this.commentToDelete);
                } else if (this.popupType === 'deleteAll') {
                    this.removeAllComments();
                } else {
                    this.$emit('update:activeVersion', this.switchActiveVersion);
                    this.switchActiveVersion = null;
                    this.isEditing = false;
                }
            }
        },

        closeContextMenu() {
            this.showContextMenu = false;
        },

        formateDate(date) {
            return moment(date).format('DD/MM/YYYY');
        },

        goToHome() {
            this.$router.push({ name: 'NarrativeHome' });
        },

        editPr(activeVersion) {
            this.isEditing = true

            if (activeVersion.startsWith('version')) {
                const versionNumber = Number(activeVersion.replace('version', ''))

                const versionObj = this.generatedVersions.find(
                    v => Number(v.version) === versionNumber
                )

                if (!versionObj) {
                    console.warn('Version not found:', activeVersion)
                    return
                }

                this.previousVersion = versionObj.commentedContent
                this.editedVersion = versionObj.commentedContent

                this.$nextTick(() => {
                    this.editor.root.innerHTML = versionObj.commentedContent
                })

                return
            }


            switch (activeVersion) {
                case 'final':
                    this.previousVersion = this.finalNarrative;
                    this.editedVersion = this.finalNarrative;
                    this.editor.root.innerHTML = this.finalNarrative;
                    break;

                case 'spine':
                    this.previousVersion = this.shortenedContent;
                    this.editedVersion = this.shortenedContent;
                    this.editor.root.innerHTML = this.shortenedContent;
                    break;

                case 'alternate':
                    this.previousVersion = this.alternateContent;
                    this.editedVersion = this.alternateContent;
                    this.editor.root.innerHTML = this.alternateContent;
                    break;

                case 'original':
                    this.previousVersion = this.alternateContent;
                    this.editedVersion = this.alternateContent;
                    this.editor.root.innerHTML = this.alternateContent;
                    break;
                case 'mediaPitch':
                    this.previousVersion = this.mediaPitchContent;
                    this.editedVersion = this.mediaPitchContent;
                    this.editor.root.innerHTML = this.mediaPitchContent;
                    break;

                default:
                    break;

            }

        },

        handleTitleUpdate(newTitle) {
            this.narrativeName = newTitle;
            this.$emit('update-docTitle', newTitle);
        },
        cancelPrChanges() {
            if (this.previousVersion === this.editedVersion) {
                this.isEditing = false
                this.$emit('update:isEditingFinalPr', this.isEditing)
                this.$nextTick(() => {
                    this.applyComments(true)
                })
            } else {
                this.popupType = 'withoutSave'
                this.showCommonPopup = true
            }
        },
        async saveNarrative(type) {

            const currentVersion = this.activeVersion
            this.versionLock = true;
            const lockedVersion = this.activeVersion;
            try {
                this.saveLoader = true;
                


                const narrativeId = this.$route.params.narrativeId;
                if (type.startsWith('version')) {
                    const versionNumber = String(type.replace('version', ''))
                    const versionObj = this.generatedVersions.find(
                        v => v.version === versionNumber
                    )

                    if (!versionObj) return

                    const updatedContent = this.editor.root.innerHTML

                    await axiosInstance.put('/narrative/saveDraft', {
                        narrativeId: narrativeId,
                        versionNumber: versionNumber,
                        commentedContent: updatedContent,
                        lastUpdatedStage: 'COMPLETED'
                    })
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: 'Changes saved successfully',
                    });

                    versionObj.commentedContent = updatedContent
                    this.refresh += 1

                    return
                }

                const payload = {
                    narrativeId,
                    lastUpdatedStage: 'COMPLETED',

                };
                let tempVersion = '';

                switch (type) {
                    case 'final':
                        tempVersion = this.finalNarrative || this.finalNarrativeDetails?.finalizedNarrative?.narrativeData || '';
                        if (this.finalizedVersion == "ORG") {
                            payload.originalNarrativeData = this.editor.root.innerHTML;
                        }
                        else {
                            payload.humanizedNarrativeData = this.editor.root.innerHTML
                        }
                        break;

                    case 'spine':
                        tempVersion = this.shortenedContent || this.finalNarrativeDetails?.shortenNarrativeData || '';
                        payload.shortenNarrativeData = this.editor.root.innerHTML
                        break;

                    case 'alternate':
                        tempVersion = this.alternateContent || this.finalNarrativeDetails?.humanizedNarrativeData || '';
                        payload.humanizedNarrativeData = this.editor.root.innerHTML
                        break;

                    case 'original':
                        tempVersion = this.originalContent || this.finalNarrativeDetails?.generatedContent || '';
                        payload.originalNarrativeData = this.editor.root.innerHTML
                        break;
                    case 'mediaPitch':
                        tempVersion = this.mediaPitchContent || this.finalNarrativeDetails?.mediaPitchData || '';
                        payload.mediaPitchData = this.editor.root.innerHTML

                    default:
                        tempVersion = this.finalNarrative || '';
                        break;
                }

                await axiosInstance.put('/narrative/saveDraft', payload);
                Eventbus.emit('commonSnackbar', {
                    snackType: 'save',
                    message: 'Changes saved successfully',
                });

                if (type === 'final') {
                    this.finalNarrativeDetails.finalizedNarrative.narrativeData = this.editor.root.innerHTML;
                } else if (type === 'spine') {
                    this.shortenedContent = this.editor.root.innerHTML;
                }
                else if (type === 'mediaPitch') {
                    this.mediaPitchContent = this.editor.root.innerHTML;
                }
                else {
                    this.alternateContent = this.editor.root.innerHTML;
                }
                this.refresh += 1;
            } catch (error) {
                console.error('Error saving narrative:', error);
                Eventbus.emit('commonSnackbar', {
                    snackType: 'error',
                    message: 'Failed to save changes',
                });
            } finally {
                this.saveLoader = false;
                this.isEditing = false;
                this.$nextTick(() => {
                    this.activeVersion = lockedVersion;
                    this.showOriginal = lockedVersion === 'final';
                    this.versionLock = false;
                })

            }
        },

        async downloadNarrative() {
            this.downloadLoader = true;
            let payload = {
                narrativeId: this.$route.params.narrativeId,
                flowType: 'NARRATIVE',
                versionType: this.activeVersion
            }

            let filename = `${this.title || this.narrativeName}_${this.activeVersion}`;
            await downloadPr(payload, filename);
            this.downloadLoader = false
        },
        editTitle() {
            this.isTitleEditing = true;
            this.newTitle = this.originalTitle;
        },
        closeIntroDialog() {
            this.showIntroDialog = false;
        },

        openIntroDialog() {
            this.showIntroDialog = true
        },
        handleCommentAdded(newComment) {
            // 1. Create the comment object matching your frontend structure
            // Map keys to match fetchComments/template structure
            const commentToAdd = {
                id: newComment.commentId,
                comment: newComment.comments,
                highlightedText: newComment.selectedText,
                time: this.formateCommentDate(newComment.createdAt),
                versionType: this.activeVersion,
                version: this.activeVersion, // Needed for applyComments filtering
                startOffset: this.popupData?.startOffset || 0,
                endOffset: this.popupData?.endOffset || 0,
                status: 'ACTIVE'
            };

            // 2. Push to local array (Optimistic update)
            this.comments.push(commentToAdd);

            // 3. UI Updates
            this.$nextTick(() => {
                this.applyComments();        // Re-run highlighting logic
                this.showContextMenu = false; // Close popup if open
                this.rightPanelTab = 'comments'; // Switch tab to show it
            });
        },
        editComment(comment) {
            //if (this.isCommentsLocked) return;
            if (this.isActionDisabled) return;

            this.editingCommentId = comment.id;
            this.originalReplyText = comment.comment;
            this.editingReplyText = comment.comment;
            this.scrollToHighlight(comment.id, 'comment');
        },
        async saveEditedComment(comment) {
            if (!this.editingReplyText.trim()) return;

            try {
                const response = await axiosInstance.post('/comments/edit', {
                    comments: this.editingReplyText.trim(),
                    documentId: this.$route.params.narrativeId,
                    commentId: comment.id,
                    versionType: comment.version,
                    documentType: 'BUISNESS_NARRATVE'
                });

                if (response.data.success) {
                    comment.comment = this.editingReplyText.trim();
                    comment.time = this.formateCommentDate(new Date());

                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: 'Comment updated successfully'
                    });

                    this.cancelEditComment();
                }
            } catch (error) {
                console.error("Failed to edit comment", error);
                Eventbus.emit('commonSnackbar', {
                    snackType: 'error',
                    message: 'Failed to save comment'
                });
            }
        },
        cancelEditComment() {
            this.editingCommentId = null;
            this.editingReplyText = '';
            this.originalReplyText = '';
        },
        clearHighlightById(id) {
            const container = this.$refs.detailsContainer
            if (!container) return

            container
                .querySelectorAll(`span[data-id="${id}"]`)
                .forEach(span => {
                    const text = document.createTextNode(span.textContent)
                    span.replaceWith(text)
                })
        },
        async deleteComment(commentId) {
            //if (this.isCommentsLocked) return;
            if (this.isActionDisabled) return;
            this.isDeleting = true;
            const originalComments = [...this.comments];

            // Optimistic UI update
            this.clearHighlightById(commentId);
            const index = this.comments.findIndex(c => c.id === commentId);
            if (index > -1) {
                this.comments.splice(index, 1);
            }
            if (this.activeCommentId === commentId) {
                this.activeCommentId = null;
            }

            try {
                const response = await axiosInstance.post('/comments/delete', {
                    documentId: this.$route.params.narrativeId,
                    versionType: this.activeVersion,
                    commentId: commentId,
                    status: "DELETED",
                    documentType: 'BUISNESS_NARRATVE'
                });

                if (!response.data.success) {
                    throw new Error("Failed to delete");
                }

                Eventbus.emit('commonSnackbar', {
                    snackType: 'delete',
                    message: 'Comment deleted successfully',
                });

            } catch (error) {
                console.error("Failed to delete comment", error);

                // Revert changes on failure
                this.comments = originalComments;
                this.$nextTick(() => {
                    this.applyComments(); // Re-apply highlights
                });

                Eventbus.emit('commonSnackbar', {
                    snackType: 'error',
                    message: 'Failed to delete comment',
                });
            } finally {
                this.isDeleting = false;
            }
        },
        async removeAllComments() {
            //if (this.isCommentsLocked) return;
            if (this.isActionDisabled) return;
            if (!this.comments.length) return;

            try {
                this.pageLoader = true;
                const response = await axiosInstance.post('/comments/delete/all', {
                    documentId: this.$route.params.narrativeId,
                    versionType: this.activeVersion,
                    documentType: 'BUISNESS_NARRATVE'
                });

                if (response.data.success) {

                    this.comments.forEach(comment => {
                        this.clearHighlightById(comment.id);
                    });
                    this.comments = [];

                    Eventbus.emit('commonSnackbar', {
                        snackType: 'delete',
                        message: 'All comments deleted successfully',
                    });
                } else {
                    throw new Error("Failed to delete comments");
                }
            } catch (error) {
                console.error("Failed to delete all comments", error);

                Eventbus.emit('commonSnackbar', {
                    snackType: 'error',
                    message: 'Failed to delete all comments',
                });
            } finally {
                this.pageLoader = false;
            }
        },
        formateCommentDate(date) {
            return moment(date).format('Do MMM, YYYY HH:mm');
        },
        closeCommentInfo() {
            this.showCommentInfo = false
        },
        closeContextMenu() {
            this.showContextMenu = false;
        },
        async fetchComments() {
            try {
                const response = await axiosInstance.post('/comments', {
                    documentId: this.$route.params.narrativeId,
                    versionType: this.activeVersion,
                    documentType: 'BUISNESS_NARRATVE'
                });

                if (response.data.success) {
                    this.comments = response.data.data.sort((a, b) => a.createdAt - b.createdAt).map(c => ({
                        id: c.commentId,
                        time: this.formateCommentDate(c.createdAt),
                        highlightedText: c.selectedText,
                        comment: c.comments,
                        startOffset: c.startOffset,
                        endOffset: c.endOffset,
                        version: c.versionType,
                        ...c
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch comments", error);
                this.comments = [];
            }
        },
        async generateCommentsVersion() {
            try {
                this.pageLoader = true;
                const response = await axiosInstance.post('/comments/generate', {
                    documentId: this.$route.params.narrativeId,
                    versionType: this.activeVersion,
                    moduleType: 'NAR'
                });
                if (!response.data.success) return;

                const newVersion = response.data.data;

                this.versionLock = true;

                this.localCommentsVersions = [
                    ...this.localCommentsVersions,
                    newVersion
                ];

                this.currentVersionIndex = this.localCommentsVersions.length - 1;
                this.activeVersion = `version${newVersion.version}`;
                this.showOriginal = false;

                this.$nextTick(() => {
                    this.applyComments(true);
                    this.versionLock = false;
                });

                Eventbus.emit('commonSnackbar', {
                    snackType: 'save',
                    message: `Version ${newVersion.version} generated successfully`
                });
            } catch (error) {
                console.error("Failed to generate comments version", error);
                Eventbus.emit('commonSnackbar', {
                    snackType: 'error',
                    message: 'Failed to generate version'
                });
            } finally {
                this.pageLoader = false;
            }
        },


        handleDeleteComment(commentId) {
            this.commentToDelete = commentId;
            this.popupType = 'deleteComment';
            this.showCommonPopup = true;
        },

        handleRemoveAllComment() {
            this.popupType = 'deleteAll';
            this.showCommonPopup = true;
        },
        canEditSelectedVersion() {
            if (!this.comments || this.comments.length === 0) {
                return true
            }

            if (this.activeVersion === 'final') {
                return this.generatedVersions.length > 0
            }

            if (this.activeVersion.startsWith('version')) {
                const currentVersionNumber = Number(
                    this.activeVersion.replace('version', '')
                )

                const currentIndex = this.generatedVersions.findIndex(
                    v => Number(v.version) === currentVersionNumber
                )
                if (currentIndex === -1) return false

                return currentIndex < this.generatedVersions.length - 1
            }

            return false
        }
    },

    mounted() {
        this.editor = new Quill(this.$refs.editor, {
            theme: 'snow',
        });
        this.editor.on('text-change', () => {
            this.editedVersion = this.editor.root.innerHTML;
        });

        this.documentData = {
            documentId: this.$route.params.narrativeId,
            documentName: this.finalNarrativeDetails?.narrativeName,
            documentType: 'BUISNESS_NARRATVE',
            imageUrl: require('@/assets/icons/narrative.svg')
        };

        this.toggleNarratives('final');
        this.fetchComments();
        this.localCommentsVersions =
            this.modelValue?.commentsVersion
                ? [...this.modelValue.commentsVersion]
                : [];
    },


}
</script>

<style scoped>
.dynamic-popup {
    width: 100%;
    background-color: white;
    border-radius: 12px;
    padding: 2em;
    display: flex;
    flex-direction: column;
    align-items: left;
    gap: 2em;
    font-family: 'Inter', sans-serif;
}

.popup-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.popup-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.popup-title span {
    font-size: 28px;
    background: white;
    color: #171717;
    font-weight: 600;
}

.popup-header .dynamic {
    width: 40px;
    height: 40px;
    color: #030712;
}

.popup-subtitle {
    color: #737373;
    font-weight: 500;
    font-size: 16px;
}

.popup-content .title {
    color: black;
}

.popup-content {
    background-color: #F5F5F5;
    padding: 8px;
    color: #737373;
    display: flex;
    flex-direction: column;
    gap: 8px;

}

.footer-btn {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 8px;
}

.standard {
    border-radius: 8px;
    align-items: center;
    padding: 8px;
    justify-content: center;
    display: flex;
    flex-direction: column;
    flex: 1;
    border: 1px solid black;
}

.standard-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 400;
    font-size: 16px;
    color: black;
    background-color: white;
}

.fine-tune {
    border-radius: 8px;
    display: flex;
    padding: 8px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #030712;
    flex: 1;
    color: var(--secondary-font-color);
}

.fine-tune-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 400;
    font-size: 16px;
    background-color: #030712;
    color: white;
}

.completed-narrative-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 2rem 9rem 2rem 13rem;
    justify-content: space-between;
    gap: 1rem;
    will-change: transform;
    overflow: hidden;
}

.completed-narrative-container.not-complete {
    justify-content: center;
}

.final-narrative-container {
    display: flex;
    flex-direction: row;
    will-change: transform;
    gap: 2.5rem;
    min-height: 0;
    flex: 1;
    height: 100%;
}

.final-narrative-wrapper {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 1rem;
    font-family: 'Inter', sans-serif;
}

.arrow-btn {
    background: transparent;
    border: none;
    font-size: 18px;
    color: var(--primary-font-color);
    cursor: pointer;
    width: 1.5rem;
}

.arrow-btn img {
    filter: brightness(0) invert(1);
}

.arrow-btn.inverted img {
    filter: none;
    opacity: 1;
}

.arrow-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.final-narrative-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5em;
    background: var(--primary-gradient-with-black);
    padding: 1rem 1.5rem;
    border-radius: 8px;
    color: var(--secondary-font-color);
    justify-content: space-between;
}

.narrative-final-header-left {
    display: flex;
    gap: 1rem;
    align-items: center;
}

.narrative-final-header-right {
    display: flex;
    flex-direction: row;
    gap: 1rem;
}

.narrative-final-btn-wrapper {
    display: flex;
    gap: 0.5em;
    justify-content: center;
    background: #FFFFFF1F;
    border: 1px solid #FFFFFF33;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: var(--normal-font-size);
}

.download-wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5em;
    justify-content: center;
    align-items: center;
}

.completed-msg {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: white;
}

.completed-title {
    color: #FFFFFF;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    flex-direction: column;
}

.top-text {
    font-size: var(--header-font-size);
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
}

.bottom-text {
    font-size: var(--normal-font-size);
    text-align: left;
    color: #FFFFFF99;
    font-style: italic;
}

.header-right {
    display: flex;
    gap: 1em;
}

.left-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;

}

.edit-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid #A3A3A3;
    background: white;
}

.save-changes-btn {
    width: 11rem;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    gap: 0.7rem;
    border-radius: 1.5rem;
    background: white;
    color: #262626;
    border: 1px solid #A3A3A3;
}

.narrative-details {
    display: flex;
    flex-direction: row;
    gap: 2em;
    padding: 3em;
    align-items: left;
    background-color: white;
    border-radius: 24px;
    min-height: 0;
    scrollbar-width: none;
    overflow-y: auto;
    flex: 2
}

.narrative-details::-webkit-scrollbar {
    width: 0;
    height: 0;
}


.narrative-details :deep(::selection) {
    background: #FF780029;
}

.details-title {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #171717;
}

.narrative-title {
    font-weight: 600;
    font-size: 24px;
}

.completed-narrative-header {
    display: flex;
    justify-content: space-between;
}

.completed-narrative-header-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
}

.doc-name-editor-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
    color: white;
}

.narrative-title {
    font-weight: 500;
    text-align: center;
}

.edit-title-input {
    width: 200px;
    align-items: center;
}

.doc-name-editor-wrapper .edit-btn {
    display: flex;
    border-radius: 8px;
    border: 1px solid #FFFFFF33;
    align-items: center;
    cursor: pointer;
    background: none;
}

.created-wrapper {
    color: #FFFFFF80;
    font-size: 14px;
    font-weight: 400;
}

.white-text {
    color: white;
}

.bold-text {
    font-weight: 500;
}

.completed-narrative-header-right {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    justify-content: flex-end;
}

.right-container {
    display: flex;
    flex-direction: column;
    flex: 1.1;
    gap: 2rem;
}

.meta-data-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    color: var(--secondary-font-color);
    border-radius: 12px;
}

.meta-data-header {
    padding: 1rem 1.5rem;
    border-bottom: 2px solid #FFFFFF12
}

.assets-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.text-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 1rem;
}

.assets-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background-color: #FFFFFF13;
    border-radius: 8px;
    padding: 0.5rem 1rem;
}

.asset-left {
    display: flex;
    flex-direction: row;
    gap: 1rem;
}

.meta-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.meta-data {
    color: #FFFFFF99;
}

.overlay {
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    z-index: 9999;
}

.version-toggle-container {
    display: flex;
    width: 67%;
    justify-content: space-between;
}

.container {
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: 0;
    gap: 2rem;
}

.dropdown-wrapper {
    position: relative;
    display: inline-block;
}


/* Original button */
.shorten {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    gap: 8px;
    border-radius: 24px;
    border: 1px solid #FFFFFF33;
    color: white;
    cursor: pointer;
    font-size: 16px;
    background: transparent;
}

.dropdown-menu {
    position: absolute;
    top: 48px;
    right: 0;
    width: 260px;
    background: var(--secondary-font-color);
    color: black;
    border-radius: 16px;
    padding: 12px 0;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
}

.dropdown-title {
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    color: #737373;
    border-bottom: 1px solid #EEEEEE;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #EEEEEE;
    cursor: pointer;
}

.dropdown-item img {
    width: 22px;
    height: 22px;
}

.dropdown-item:last-child {
    border-bottom: none;
}

.version-toggle {
    display: flex;
    gap: 8px;
    border-radius: 5rem;
    padding: 4px;
}

.toggle-btn {
    padding: 8px 20px;
    border: none;
    background: #FFFFFF33;
    color: #E5E5E5;
    cursor: pointer;
    border-radius: 5rem;
    font-size: var(--normal-font-size);
    display: flex;
    align-items: center;
}

.toggle-btn.active {
    background: white;
    color: var(--primary-font-color);
}

.toggle-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.narrative-details-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex: 2;
}

.drag-popup {
    position: fixed;
    top: calc(var(--mouse-y) + 20px);
    left: calc(var(--mouse-x) + 20px);
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding: 12px;
}

.popup-option {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px;

    &:hover {
        background: #f5f5f5;
    }
}

:deep(.highlight-comment) {
    background: #ff780029;
    border-radius: 4px;
    padding: 0 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
}

:deep(.highlight-comment:empty) {
    display: none;
}

:deep(.highlight-comment.active-comment-highlight) {
    background: pink;
}

:deep(.highlight-comment.active) {
    background: #ff780080;
}

.right-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: #ffffff12;
    border-radius: 8px;
    min-height: 0;
}

.right-panel .panel-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.right-panel .panel-content::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.right-panel .comments-panel.panel-content {
    gap: 0;
    padding-top: 0;
}

.right-panel-tabs {
    display: flex;
    background: #ffffff13;
    justify-content: center;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 1px solid #FFFFFF1A;
}

.tab-item {
    flex: 1;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 0;
    font-size: var(--normal-font-size);
    color: #FFFFFF80;
    cursor: pointer;
    position: relative;
    gap: 8px;
}

.tab-item.active {
    color: #FFFFFF;
}

.tab-item.active::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -1px;
    width: 100%;
    height: 2px;
    background-color: #FFFFFF;
}

.panel-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1.5rem;
    padding: 1rem;
}

.preview-img {
    width: 16px;
    height: 14px;
}

.details-wrapper {
    gap: 1rem;
    color: var(--secondary-font-color);
    border-radius: 12px;
    border: 1px solid #FFFFFF1A;
}

.details-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
}

.comments-panel {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
    overflow: hidden;
}

.empty-comments {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 1rem;
    margin: auto;
    color: #FFFFFFCC;
}

.empty-comments-img {
    width: 30px;
    height: 30px;
     align-items: center;
}


.empty-title {
    font-size: 24px;
    font-weight: 700;
    color: #FFFFFF;
    font-family: 'OverpassMono';
}

.empty-desc {
    font-size: 14px;
    color: #FFFFFF99;
}

.tour-image {
    width: 350px;
    height: 200px;
    border-radius: 8px;
    padding: 1rem;
}

.header-title {
    font-size: 16px;
    font-weight: 500;
    color: #FFFFFF;
}

.meta-data-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    color: var(--secondary-font-color);
    border-radius: 12px;
}

.empty-comments-info {
    display: flex;
    align-items: center;
    text-align: left;
    gap: 8px;
    background: #FFFFFF14;
    border-radius: 8px;
    padding: 12px;
    font-size: 14px;
    color: #FFFFDDD3;
}

.empty-comments-info img {
    width: 16px;
    height: 16px;
    align-items: center;
}

.comments-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
    padding-right: 6px;
    flex: 1;
    min-height: 0;
}

.comments-list::-webkit-scrollbar {
    width: 4px;
}

.comments-list::-webkit-scrollbar-thumb {
    background: #ffffff33;
    border-radius: 4px;
}

.comment-card {
    background: #FFFFFF14;
    border: 1px solid #FFFFFF1F;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    overflow: hidden;
    flex-shrink: 0;
}

.comment-card.active {
    border: 1px solid #FFFFFF1F;
}

.comment-card.active .comment-header {
    background: #FFFFFF33;
}

.comment-header {
    display: flex;
    flex-direction: column;
    background: #FFFFFF13;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 0.5rem;
    gap: 0.25rem;
}

.comment-top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

}

.comment-time {
    font-size: 12px;
    color: #FFFFFF80;
}

.comment-text {
    font-size: 14px;
    color: #FFFFFF;
    line-height: 1.4;
}

.comment-actions {
    display: flex;
    gap: 8px;
}

.icon-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #FFFFFF1A;
    border: 1px solid #FFFFFF33;
    cursor: pointer;
}

.comment-highlight {
    font-size: 14px;
    font-weight: 500;
    color: #FFFFFF;
    line-height: 1.4;
    padding-top: 0.25rem;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;

}

.icon-btn img {
    width: 14px;
    height: 14px;
}

.comment-actions img {
    width: 16px;
    height: 16px;
    cursor: pointer;
    opacity: 0.85;
}

.comment-actions img:hover {
    opacity: 1;
}

.comment-reply {
    display: flex;
    gap: 8px;
    font-size: 13px;
    color: #FFFFFFCC;
    padding: 0.5rem;
    text-align: center;
    align-items: center;
}

.reply-arrow {
    opacity: 0.7;
    line-height: 1.4;
    color: var(--secondary-font-color);
}


.reply-text {
    line-height: 1.4;
}

.reply-textarea {
    width: 100%;
    resize: none;
    background: #ffffff14;
    border: 1px solid #ffffff33;
    border-radius: 6px;
    padding: 8px;
    font-size: 13px;
    color: white;
    outline: none;
}



.reply-text {
    line-height: 1.4;
}

.comment-actions-bar {
    display: flex;
    gap: 12px;
    padding: 16px;
    border-top: 1px solid #ffffff1a;
    background-color: #FFFFFF13;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    margin-top: auto;
}


.primary-btn {
    display: flex;
    flex: 1;
    background: var(--primary-gradient-with-black);
    color: #FFFFFF;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    gap: 6px;
}

.primary-btn-image {
    height: 18px;
    width: 18px;
}

.secondary-btn {
    flex: 1;
    background: transparent;
    border: 1px solid #FFFFFF33;
    color: #FFFFFFCC;
    border-radius: 8px;
    padding: 0.5rem;
    cursor: pointer;
}

.pr-final-header-right {
    display: flex;
    flex-direction: row;
    gap: 2rem;
}

.pr-final-btn-wrapper {
    display: flex;
    gap: 0.5em;
    justify-content: center;
    background: #FFC6FB;
    border: 1px solid #FFFFFF33;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    color: var(--primary-font-color);
    font-size: var(--normal-font-size);
    cursor: pointer;
}


.comments-intro {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 12px;
    padding: 20px;
    gap: 10px;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.popup-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: var(--header-font-size);
}

.close-btn {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.popup-subtitle {
    font-size: var(--normal-font-size);
    color: #6b7280;
}

.popup-content img {
    width: 100%;
    border-radius: 8px;
}

.comment-input-popup {
    background: white;
    border-radius: 8px;
    border: 2px solid #E5E5E5;
    overflow: hidden;
}

.popup-title {
    color: #737373;
    border-bottom: 1px solid #E5E5E5;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
}

.popup-body {
    padding: 12px;
}

.comment-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #E5E5E5;
    border-radius: 6px;
    font-family: inherit;
    font-size: 14px;
    resize: vertical;
    min-height: 80px;
    outline: none;
    transition: border-color 0.2s;
}

.comment-input::placeholder {
    color: #A3A3A3;
}

.popup-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 8px;
    border-top: 1px solid #E5E5E5;
    background: #FAFAFA;
}

.action-btn {
    flex: 1;
    padding: 8px 16px;
    border-radius: 6px;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.action-btn.discard {
    background: white;
    color: #030712;
    border: 1px solid #030712;
}

.action-btn.discard:hover {
    background: #F5F5F5;
}

.action-btn.add {
    background: #030712;
    color: white;
}

.action-btn.add:hover:not(:disabled) {
    opacity: 0.9;
}

.action-btn.add:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.narrative-page-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
}



.comment-info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #FFFFFF;
    padding: 12px;
    font-size: 14px;
    color: #030712;
    margin-bottom: 1rem;
}

.comment-info img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.version-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
}

.version-controls .nav-arrow {
    cursor: pointer;
    font-weight: bold;
    padding: 0 4px;
    user-select: none;
}

.version-controls .nav-arrow.disabled {
    opacity: 0.3;
    pointer-events: none;
}

.version-controls .version-label {
    cursor: pointer;
    min-width: 80px;
    text-align: center;
}

.comment-left {
    display: flex;
    gap: 1rem;
}

.completed-msg {
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: black;
    font-size: 12px;
}

.cancel-img {
    width: 10px;
    height: 10px;
    padding: 2px;
}

.edit-tooltip-content {
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
}

/* Remove Vuetify default tooltip surface */
.edit-tooltip-content::before {
    background: transparent !important;
}

.edit-tooltip-text {
    color: var(--secondary-font-color);
    font-size: 12px;
    line-height: 1.4;
}
</style>